import { setProvider, setAnalyticsDebug, registerSuperProperties } from "common/analytics";
import { webMixpanelProvider } from "./mixpanelProvider";
import { registerPermissionSuperProps } from "./superProps";
import { resolveDeviceFormFactor } from "./device";

// Guard so this can be safely called from BOTH the client entry and __root (idempotent).
let analyticsInitialized = false;

/**
 * Wires the new Mixpanel pipeline on the client. Replaces the disabled legacy chain
 * (mixpanel-client.ts -> services/analytics.ts). Stays unconfigured (silent no-op) when the
 * new token is absent, preserving the "fire nothing until configured" guarantee.
 */
export function initWebAnalytics(): void {
  if (typeof window === "undefined") return; // SSR guard
  if (analyticsInitialized) return; // idempotent: safe from entry and/or __root
  analyticsInitialized = true;

  const token = import.meta.env.VITE_MIXPANEL_TOKEN_V2 ?? "";
  if (!token) return; // no NEW token -> do not register a provider

  webMixpanelProvider.init(token, {
    api_host: import.meta.env.VITE_MIXPANEL_HOST || undefined, // EU data-residency project
    track_pageview: false,
    persistence: "localStorage",
    debug: import.meta.env.DEV, // local-only: logs every mixpanel.track() to the console
  });
  setProvider(webMixpanelProvider);
  setAnalyticsDebug(import.meta.env.DEV);

  // Baseline super properties. product_type discriminates the consumer web app from the business
  // portal / widget (which share generated event names); device is the viewport form factor
  // (apps/web is responsive). dark_mode/auth_state are registered reactively by
  // useAnalyticsSuperProperties (RootLayout), which also keeps device live; page_name on route change.
  // (user_type is now a people property, also set there via setUserProperties — no longer a super prop.)
  registerSuperProperties({
    product_type: "app",
    device: resolveDeviceFormFactor(),
  });
  void registerPermissionSuperProps(); // async browser permission query (location + notification)
}
